import {Observable, of, ReplaySubject} from 'rxjs';

import {catchError, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import * as _ from 'lodash';
import {UserId} from '../types/ids';
import {AuthApi} from '../api/rest/auth.restapi';
import {WebmateOptions} from '../../webmate-options';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {GenericApiResponse} from "@webmate-web-core/shared/api/apibase.api";
import {encodeReturnUrl} from '@webmate-web-core/shared/utils/route-access';

// TODO refactor auth code to remove {} as default value
export interface AuthToken {
    access_token?: string,
    expires_in?: number,
    refresh_token?: string,
    token_type?: string,
    impersonationMode?: boolean
}

export interface LogoutOptions {
    preserveReturnUrl?: boolean;
}

export abstract class AuthStorageStrategy {
    abstract isSameAuthInfo(tokenA: AuthToken, tokenB: AuthToken): boolean;

    /** Returns the AuthInfo-Object or {} if it does not exist. Returns the value RIGHT NOW, you are better of subscribing via 'observeAuthInfo' */
    abstract getAuthInfo(): AuthToken;

    abstract storeAuthInfo(authInfo: AuthToken, informOtherTabs: boolean, self?: LocalStorageAuthStorageStrategy): void;

    abstract deleteAuthInfo(): void;

    /** Can be used to observe changes to the authInfo object. */
    abstract observeAuthInfo(): Observable<AuthToken>;
}

/**
 * This AuthStorageStrategy bases on the idea presented at
 * https://blog.guya.net/2015/06/12/sharing-sessionstorage-between-tabs-for-secure-multi-tab-authentication/.
 * The basic idea is to make AuthInfo available to other tabs by communicating via events (only visible to tabs of same domain).
 * The problem tackled here is that we want to avoid having the user to log in in every tab where webmate is open.
 *
 * Some invariants:
 * -
 */
@Injectable()
export class LocalStorageAuthStorageStrategy extends AuthStorageStrategy {

    private readonly LOCALSTORAGE_AUTHINFO_KEY = '_webmate_authinfo';
    private readonly LOCALSTORAGE_AUTHINFO_REQUEST = '_webmate_authinfo_request';
    private readonly SESSIONSTORAGE_AUTHINFO_KEY = '_webmate_authinfo';

    private _authInfo: AuthToken = {};
    private _currentSubjectValue?: AuthToken = undefined;
    private _authInfoSubject: ReplaySubject<AuthToken> = new ReplaySubject(1);

    isSameAuthInfo(tokenA: AuthToken, tokenB: AuthToken): boolean {
        return tokenA['expires_in'] === tokenB['expires_in'] && tokenA['refresh_token'] === tokenB['refresh_token'] && tokenA['access_token'] === tokenB['access_token'];
    }

    constructor(private persistenceService: PersistenceService) {
        super();
        /** Handles the LocalStorage-Events, aka the ACTUAL MAIN FUNCTIONALITY logic of this Service */
        window.addEventListener('storage', event => {
            if (event.newValue == null) return; //these come from our subsequent removal of the Keys from the Storage, always ignore them!

            if (event.key === this.LOCALSTORAGE_AUTHINFO_REQUEST) {
                if (!_.isEmpty(this.getAuthInfo())) {
                    /** if we are not logged in, we should NEVER try to persuade others of our superior credentials. Therefore the check above ;-) */
                    this._shareAuthInfoWithOtherTabs();
                }
            } else if (event.key === this.LOCALSTORAGE_AUTHINFO_KEY) {
                /** some other Tab tells us that it has Credentials! */
                if ((event.newValue !== undefined) && (event.newValue !== null)) {
                    let newAuthInfo = JSON.parse(event.newValue);
                    if (!this.isSameAuthInfo(newAuthInfo, this._authInfo as AuthToken)) {
                        this.storeAuthInfo(newAuthInfo, false);
                    }
                }
            }
        });

        if (_.isEmpty(this.getAuthInfo())) {
            /** request auth info from other tabs if we have no idea */
            this._requestAuthInfoFromOtherTabs();

            //we basically cannot set the AuthInfo here because we need a cycle to receive it from the other Tab, hence we employ a Timeout...
            let storeAuthInfoHelper = (self: LocalStorageAuthStorageStrategy) => {
                if (_.isEmpty(self._authInfo)) {
                    /** inform all other Services that listen to us. Our AuthInfo might be empty at this point of course, if we are not authenticated. */
                    this.storeAuthInfo(this._authInfo, false, self);
                }
            };
            setTimeout(storeAuthInfoHelper, 100, this);
        } else {
            /** we have auth-info, make sure our subscribers are informed as well */
            this.storeAuthInfo(this._authInfo, false);
        }
    }

    getAuthInfo(): AuthToken {
        if (_.isEmpty(this._authInfo)) { // self._authInfo is only the cached value of the sessionstore value
            let storedAuthInfo = this.persistenceService.getSessionItem(this.SESSIONSTORAGE_AUTHINFO_KEY);
            if (storedAuthInfo !== null && storedAuthInfo !== undefined && !_.isEmpty(storedAuthInfo)) {
                this._authInfo = JSON.parse(storedAuthInfo);
            }
        }
        return this._authInfo;
    }

    storeAuthInfo(authInfo: AuthToken, informOtherTabs: boolean, self?: LocalStorageAuthStorageStrategy) {
        self = self ?? this;
        self._authInfo = {...authInfo};
        this.persistenceService.setSessionObjectItem(self.SESSIONSTORAGE_AUTHINFO_KEY, self._authInfo);
        if (informOtherTabs) {
            self._shareAuthInfoWithOtherTabs();
        }
        /* fire the Subject, so that all other UseCases are informed */
        if (self._currentSubjectValue !== self._authInfo) {
            self._currentSubjectValue = self._authInfo;
            self._authInfoSubject.next(self._authInfo);
        }

    }

    deleteAuthInfo() {
        this.storeAuthInfo({}, true);
    }

    private _requestAuthInfoFromOtherTabs() {
        this.persistenceService.setItem(this.LOCALSTORAGE_AUTHINFO_REQUEST, Date.now().toString()); //fires event
        this.persistenceService.removeItem(this.LOCALSTORAGE_AUTHINFO_REQUEST); //remove afterwards, event has been processed
    }

    private _shareAuthInfoWithOtherTabs() {
        this.persistenceService.setItem(this.LOCALSTORAGE_AUTHINFO_KEY, JSON.stringify(this.getAuthInfo())); //fires event
        this.persistenceService.removeItem(this.LOCALSTORAGE_AUTHINFO_KEY); //remove afterwards, event has been processed
    }

    observeAuthInfo(): Observable<AuthToken> {
        return this._authInfoSubject.asObservable();
    }

}

/**
 * OAuth2AuthService that uses an OAuth2 Authorization service provided by the webmate API.
 */
@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private isLoggingOut = false;

    constructor(private api: AuthApi, protected authStorageStrategy: AuthStorageStrategy, private webmateOptions: WebmateOptions, private router: Router) {
    }

    authenticateUserWithPasswordGrant(username: string, password: string): Observable<boolean> {
        return this.api.getAuthToken(username, password).pipe(map(token => {
            this.authStorageStrategy.storeAuthInfo(token, true);
            return true;
        }), catchError(err => {
            return of(false);
        }));
    }

    /** Adds the bearer-token / authentication information as Query Parameter to the given URL. */
    addAuthAsQueryParameter(url: string): string {
        let authToken = this.authStorageStrategy.getAuthInfo()['access_token'];

        let urlObj = new URL(url);
        urlObj.searchParams.set('access_token', authToken!);
        return urlObj.toString();
    }

    /**
     * Returns the current authentication token, or undefined if no authentication is present.
     * WARNING: exposing the token might be a security issue, use with care!
     */
    getAuthToken(): string | undefined {
        return this.authStorageStrategy.getAuthInfo()['access_token'];
    }

    /** same as addAuthAsQueryParameter, but also prepends the baseUri */
    makeAuthorizedUrl(path: string): string {
        return this.addAuthAsQueryParameter(this.webmateOptions.versionedApiUri + path);
    }

    makeUnauthorizedUrl(path: string): string {
        return this.webmateOptions.versionedApiUri + path;
    }

    /** same as addAuthAsQueryParameter, but also prepends the baseUri */
    makeAuthorizedWebSocketUrl(path: string): string {
        return this.addAuthAsQueryParameter(this.webmateOptions.websocketUri + path);

    }

    /** If true, the current webmate application is authenticated with a user. */
    private isAuthenticated(): boolean {
        const a = this.authStorageStrategy.getAuthInfo();
        return a !== null && !_.isEmpty(a);
    }

    logout(options: LogoutOptions = {}) {
        if (this.isLoggingOut) {
            // Reacting to deauthenticate() below can synchronously re-enter logout(); ignore the re-entrant call.
            return;
        }
        this.isLoggingOut = true;
        this.deauthenticate();
        this.router.navigate(this.getLoginUrlPath(), {queryParams: this.createLogoutQueryParams(options)}).then(() => {
            window.location.reload();
        }).catch(() => {
            this.isLoggingOut = false;
        });
    }

    getLoginUrlPath(): string[] {
        if (this.webmateOptions.instance === 'openshift-webmate-atruvia') {
            return['login', 'sso'];
        } else {
            return['login'];
        }
    }

    private deauthenticate() {
        this.authStorageStrategy.deleteAuthInfo();
    }

    private createLogoutQueryParams(options: LogoutOptions): {returnUrl?: string} | undefined {
        if (!options.preserveReturnUrl || !this.shouldPreserveReturnUrl(this.router.url)) {
            return undefined;
        }
        return {
            returnUrl: encodeReturnUrl(this.router.url)
        };
    }

    private shouldPreserveReturnUrl(url: string): boolean {
        return !!url && url !== '/' && url !== '/projects' && !url.startsWith('/login');
    }

    /** Notifies about changes in the authorization status. */
    observeAuthStatus(): Observable<boolean> {
        return this.authStorageStrategy.observeAuthInfo().pipe(map(whatever => {
            return this.isAuthenticated();
        }));
    }

    isInImpersonationMode(): boolean {
        return !!this.authStorageStrategy.getAuthInfo()?.impersonationMode;
    }

    activateUserAccountWithToken(token: string): Observable<UserId> {
        return this.api.activateUserAccount(token);
    }

    /**
     * Connects to the webmate backend and trigger a token refresh if necessary.
     */
    probeAuthentication(): Observable<void> {
        return this.api.probeAuthentication().pipe(catchError(err => of(undefined)));
    }

    requestResetPassword(email: string, activationLink: string): Observable<any> {
        return this.api.requestResetPassword(email, activationLink);
    }

    resetPassword(password: string, token: string): Observable<any> {
        return this.api.resetPassword(password, token);
    }

    applyChangeEmailToken(token: string): Observable<GenericApiResponse> {
        return this.api.applyChangeEmailToken(token);
    }

    impersonateUser(userId: UserId, reason: string): Observable<boolean> {
        return this.api.impersonateUser(userId, reason).pipe(map(authInfo => {
            authInfo.impersonationMode = true;
            this.authStorageStrategy.storeAuthInfo(authInfo, false);
            return true;
        }), catchError(err => {
            return of(false);
        }));
    }

    registerCustomer(email: string, username: string, organization: string, agreesToMarketing: boolean): Observable<any> {
        return this.api.registerCustomer(email, username, organization, {agreesToMarketing: agreesToMarketing});
    }

    getAuthRealmForDomain(domain: string): Observable<any> {
        return this.api.getAuthRealmForDomain(domain);
    }
}
